import { Phone, MessageCircle, MapPin, Star, ShieldCheck, Zap } from "lucide-react";
import { BUSINESS, telLink, waLink } from "../lib/business";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden pt-20 pb-24 lg:pt-28 lg:pb-36"
    >
      {/* Background image with dark overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1758411897888-3ca658535fdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzc2NzQ5NjEwfDA&ixlib=rb-4.1.0&q=85"
          alt="لوحة عدادات سيارة فاخرة مع تكييف"
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        <div className="absolute inset-0 bg-gradient-to-l from-slate-950 via-slate-950/60 to-transparent" />
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute top-40 -left-20 w-[420px] h-[420px] rounded-full bg-cyan-400/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Text */}
          <div className="lg:col-span-7 reveal">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-[13px] font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              ورشة متخصصة في قلب الرياض — حي الخليج
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[64px] leading-[1.1] font-extrabold tracking-tight text-white">
              خبرة حقيقية في
              <br />
              <span className="bg-gradient-to-l from-cyan-300 via-cyan-400 to-cyan-500 bg-clip-text text-transparent">
                تكييف وكهرباء السيارات
              </span>
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
              تشخيص دقيق، شغل محترم، ومصداقية يشهد لها العملاء. متخصصون في إصلاح
              أعطال المكيفات والكهرباء لجميع أنواع السيارات في الرياض.
            </p>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={telLink}
                data-testid="hero-call-button"
                className="group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold transition-all shadow-[0_8px_30px_rgba(6,182,212,0.35)] hover:shadow-[0_12px_40px_rgba(6,182,212,0.55)] hover:-translate-y-0.5"
              >
                <Phone className="w-4.5 h-4.5" />
                اتصل الآن
                <span className="tabular-nums text-slate-900/80 font-bold mr-1">
                  {BUSINESS.phone}
                </span>
              </a>
              <a
                href={waLink("السلام عليكم، أود حجز موعد لفحص المكيف")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold transition-all hover:-translate-y-0.5"
              >
                <MessageCircle className="w-5 h-5" />
                واتساب
              </a>
              <a
                href={BUSINESS.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-map-button"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl border border-slate-700 hover:border-cyan-500/60 text-slate-200 hover:text-cyan-300 font-medium transition-all"
              >
                <MapPin className="w-5 h-5" />
                الموقع على الخريطة
              </a>
            </div>

            {/* Quick trust */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-400" /> تشخيص أمين
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" /> إنجاز سريع
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                تقييم{" "}
                <span className="text-white font-semibold tabular-nums">
                  4.7/5
                </span>{" "}
                من <span className="tabular-nums">+474</span> عميل
              </div>
            </div>
          </div>

          {/* Stat card / visual */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-cyan-500/40 via-cyan-500/0 to-cyan-500/20 rounded-3xl blur-xl opacity-60" />
              <div className="relative rounded-3xl border border-slate-800/80 bg-slate-900/70 backdrop-blur-xl p-6 lg:p-8 shadow-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-400">تقييم العملاء</div>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-5xl font-extrabold text-white tabular-nums">
                        4.7
                      </span>
                      <span className="text-slate-400">/ 5</span>
                    </div>
                    <div className="mt-2 flex items-center gap-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < 4
                              ? "text-amber-400 fill-amber-400"
                              : "text-amber-400 fill-amber-400/70"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="mt-2 text-sm text-slate-400">
                      بناءً على{" "}
                      <span className="text-white font-semibold tabular-nums">
                        +474
                      </span>{" "}
                      مراجعة
                    </div>
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
                      <path
                        d="M12 2L14.09 8.26L20.5 9.27L15.75 13.97L17.09 20.5L12 17.27L6.91 20.5L8.25 13.97L3.5 9.27L9.91 8.26L12 2Z"
                        fill="url(#grad)"
                      />
                      <defs>
                        <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0" stopColor="#06b6d4" />
                          <stop offset="1" stopColor="#0ea5e9" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div className="text-xs text-slate-400">الخدمة</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      جميع السيارات
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div className="text-xs text-slate-400">ساعات العمل</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      من 8:30 ص
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div className="text-xs text-slate-400">الموقع</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      حي الخليج، الرياض
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800">
                    <div className="text-xs text-slate-400">التخصص</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      تكييف وكهرباء
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
